import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ConsentRecord, grantConsent as apiGrantConsent, getAllConsents } from '../api/consent';

interface ConsentState {
  consents: ConsentRecord[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ConsentState = {
  consents: [],
  isLoading: false,
  error: null,
};

export const fetchConsents = createAsyncThunk(
  'consent/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllConsents();
      return response.consents;
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

export const submitConsent = createAsyncThunk(
  'consent/submit',
  async (
    payload: { childId: string; consentType: string; version: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiGrantConsent(payload.childId, payload.consentType, payload.version);
      return response.consent;
    } catch (error: any) {
      return rejectWithValue(error.message as string);
    }
  },
);

const consentSlice = createSlice({
  name: 'consent',
  initialState,
  reducers: {
    clearConsentError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConsents.fulfilled, (state, action: PayloadAction<ConsentRecord[]>) => {
        state.isLoading = false;
        state.consents = action.payload;
      })
      .addCase(fetchConsents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(submitConsent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(submitConsent.fulfilled, (state, action: PayloadAction<ConsentRecord>) => {
        state.isLoading = false;
        // Replace or add the consent
        const idx = state.consents.findIndex(
          (c) =>
            c.childId === action.payload.childId && c.consentType === action.payload.consentType,
        );
        if (idx >= 0) {
          state.consents[idx] = action.payload;
        } else {
          state.consents.push(action.payload);
        }
      })
      .addCase(submitConsent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearConsentError } = consentSlice.actions;
export default consentSlice.reducer;
